<template>
  <div>
    <BackstageInputLabel
      :label="label"
      :label-for="name"
      :required="required"
      :class="['select-field-label', { disabled: disabled }]">
      <select
        :id="name"
        :value="inputValue"
        :name="name"
        :disabled="disabled"
        :class="[
          'select-field',
          {
            'with-arrow': !disabled,
            'option-placeholder': !inputValue,
          },
        ]"
        @blur="handleBlur"
        @change="onChange">
        <option v-if="!inputValue" value="" disabled selected hidden>
          {{ placeholder }}
        </option>
        <option v-for="opt in options" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
    </BackstageInputLabel>
    <ErrorMessage v-if="errorMessage && meta.touched" :name="name" />
  </div>
</template>

<script setup lang="ts">
import BackstageInputLabel from '@/components/common/BackstageInputLabel.vue';
import { ErrorMessage, useField } from 'vee-validate';

export interface SelectFieldOption {
  value: string;
  label: string | null;
}

interface Props {
  label?: string;
  name: string;
  modelValue: any;
  disabled?: boolean;
  required?: boolean;
  options: SelectFieldOption[];
  placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  required: false,
  placeholder: '',
  label: '',
  modelValue: '',
});

const emit = defineEmits<{
  (e: 'change', event: Event): void;
  (e: 'update:modelValue', value: string): void;
}>();

const toSelectValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
};

const {
  setValue,
  value: inputValue,
  errorMessage,
  meta,
  handleBlur,
} = useField<string>(props.name, undefined, {
  initialValue: toSelectValue(props.modelValue),
});

const onChange = (e: Event) => {
  const val = (e.target as HTMLSelectElement).value;
  setValue(val);
  emit('update:modelValue', val);
  emit('change', e);
};
</script>
<style scoped lang="scss">
.select-field-label {
  height: inherit;
}

.select-field {
  align-items: center;
  display: flex;
  width: 100%;

  &.with-arrow {
    padding: 0px 24px 0 0;
    background-position: calc(100% - 2px) center;
  }
}
</style>
